import { FormControl, InputLabel, Select, MenuItem, Box, SelectProps, FormLabel } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import FormError from './FormError';

export type SelectOption = {
    value: number | string;
    label: string;
};

type FormSelectProps = SelectProps & {
    options: SelectOption[];
    name: string;
    label: string;
    fullWidth?: boolean;
    separateLabel?: boolean;
    defaultEmpty?: boolean;
};

export default function FormSelect(props: FormSelectProps) {
    const {
        name,
        label,
        options,
        disabled,
        fullWidth,
        separateLabel,
        defaultEmpty = false,
        value: externalValue,
        ...selectInputrest
    } = props;
    const { register, formState: { errors }, watch } = useFormContext();

    const errorMessage = typeof errors[name]?.message === 'string' ? errors[name]?.message as string : undefined;

    if (!options.length) {
        return null;
    }

    const watchedValue = watch(name) === undefined ? options[0]?.value : watch(name);
    const value = externalValue || watchedValue;

    return (
        <Box>
            {separateLabel ? <FormLabel htmlFor={name}>{label}</FormLabel> : null}
            <FormControl
                fullWidth
                sx={{ marginBottom: errors[name]?.message && !disabled ? 0 : 2.5 }}
                size="small"
                disabled={disabled}
            >
                {!separateLabel ? <InputLabel id={`${name}-label`}>{label}</InputLabel> : null}
                <Select
                    label={!separateLabel ? label : undefined}
                    {...register(name)}
                    {...selectInputrest}
                    value={value}
                    data-testid={`form-select-${name}`}
                >
                    {options.map(({ value: optionValue, label: optionLabel }) => (
                        <MenuItem
                            value={optionValue}
                            key={optionValue || optionLabel}
                            data-testid={`form-select-option-${value}`}
                        >
                            {optionLabel}
                        </MenuItem>
                    ))}
                </Select>
                <FormError show={Boolean(errorMessage) && !disabled}>
                    {errorMessage}
                </FormError>
            </FormControl>
        </Box>
    );
}
